'use client';

import { Map, MapLink } from '@instamenu/ui-system-next';
import { MapPin } from 'lucide-react';
import { clsx } from 'clsx';
import * as styles from './map-preview-card.css';

type Mappable = Parameters<typeof MapLink>[0]['mappable'];

export interface MapPreviewCardProps {
  /**
   * Source of truth for the destination — same shape MapLink expects. When the
   * underlying address has lat/lon coordinates the embedded `Map` is rendered;
   * otherwise the card falls back to a static "Tap to open directions" hint.
   */
  mappable: Mappable;
  /** Tall variant for desktop two-column layouts. */
  tall?: boolean;
  /** Fallback caption shown when no map can be rendered. */
  caption?: string;
}

/**
 * Clickable preview wrapping our `Map` widget. Used by the Pickup state to give
 * the customer a shortcut to navigation apps.
 */
export const MapPreviewCard = ({
  mappable,
  tall,
  caption = 'Tap to open directions',
}: MapPreviewCardProps) => {
  const location = mappable?.address?.location;

  return (
    <MapLink mappable={mappable}>
      <div
        className={clsx(styles.card, tall && styles.desktopTall)}
        data-id="order-complete-map-preview"
      >
        {location ? (
          <div className={styles.mapHolder}>
            <Map style={{ width: '100%', height: '100%' }} lat={location.lat} lon={location.lon} />
          </div>
        ) : (
          <div className={styles.fallback}>
            <MapPin size={22} strokeWidth={1.75} />
            <span className={styles.caption}>{caption}</span>
          </div>
        )}
      </div>
    </MapLink>
  );
};
